import React, { useState } from 'react';
import { AiOutlineDown } from 'react-icons/ai';
import { Typography } from '../Typography';
import styles from './BlogSidebar.module.scss';

interface BlogSidebarProps {
  categories: any[];
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

const BlogSidebar: React.FC<BlogSidebarProps> = ({
  categories,
  selectedCategory,
  onCategoryChange,
  searchQuery,
  onSearchChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleCategoryClick = (category: string) => {
    onCategoryChange(category);
    setIsOpen(false); // Close dropdown after selection
  };

  // Get selected category name for display
  const selectedCategoryName =
    selectedCategory === 'all'
      ? 'View all'
      : categories.find((cat) => cat.slug === selectedCategory)?.name || 'Select category';

  return (
    <div className={styles.sidebar}>
      {/* Mobile/Tablet Dropdown */}
      <div className={styles.dropdownWrapper}>
        <button className={styles.dropdownButton} onClick={handleToggle}>
          <span>{selectedCategoryName}</span>
          <AiOutlineDown
            size={16}
            className={`${styles.dropdownIcon} ${isOpen ? styles.open : ''}`}
          />
        </button>

        {/* Dropdown Content */}
        {isOpen && (
          <div className={styles.dropdownContent}>
            <button
              className={`${styles.categoryItem} ${
                selectedCategory === 'all' ? styles.active : ''
              }`}
              onClick={() => handleCategoryClick('all')}
            >
              View all
            </button>

            {categories.map((category) => (
              <button
                key={category._id}
                className={`${styles.categoryItem} ${
                  selectedCategory === category.slug ? styles.active : ''
                }`}
                onClick={() => handleCategoryClick(category.slug)}
              >
                {category.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Desktop Sidebar */}
      <div className={styles.desktopSidebar}>
        {/* Search - Only visible on desktop */}
        <div className={styles.searchContainer}>
          <div className={styles.searchInput}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className={styles.searchIcon}
            >
              <path
                d="M7.33329 12.6667C10.2788 12.6667 12.6666 10.2789 12.6666 7.33333C12.6666 4.38781 10.2788 2 7.33329 2C4.38777 2 1.99996 4.38781 1.99996 7.33333C1.99996 10.2789 4.38777 12.6667 7.33329 12.6667Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 14L11.1 11.1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className={styles.input}
            />
          </div>
        </div>

        {/* Categories */}
        <div className={styles.categoriesSection}>
          <Typography
            variant="text-md"
            as="h3"
            color="primary"
            weight="bold"
            className={styles.sectionTitle}
          >
            Blog categories
          </Typography>

          <div className={styles.categoriesList}>
            <button
              className={`${styles.categoryItem} ${
                selectedCategory === 'all' ? styles.active : ''
              }`}
              onClick={() => onCategoryChange('all')}
            >
              <Typography variant="text-sm" color="primary">
                View all
              </Typography>
            </button>

            {categories.map((category) => (
              <button
                key={category._id}
                className={`${styles.categoryItem} ${
                  selectedCategory === category.slug ? styles.active : ''
                }`}
                onClick={() => onCategoryChange(category.slug)}
              >
                <Typography variant="text-sm" color="primary">
                  {category.name}
                </Typography>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogSidebar;
